/**
 * Phase 25 Plan 25-04 — pre-submit staged attachment chip (D-19).
 *
 * Image MIMEs render an inline thumbnail via `URL.createObjectURL(file)`;
 * the blob URL is revoked on unmount AND on remove to prevent leaks.
 *
 * Pitfall — forgetting to revoke leaks blob memory across composer renders.
 * The cleanup function in `useEffect` covers both unmount (parent removes
 * the chip) and the case where the same chip's `file` prop changes (very
 * unlikely with a stable key, but cheap insurance).
 */
import { useEffect, useState } from 'react';
import { XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';

const IMAGE_MIMES = new Set<string>([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

export interface AttachmentChipProps {
  file: File;
  /** Optional inline error code from a failed attachment upload. */
  errorCode?: string | null;
  errorMessage?: string | null;
  /** Called when the user clicks the ✕ to remove the staged file. Omit to hide the remove button. */
  onRemove?: () => void;
}

export function AttachmentChip({
  file,
  errorCode,
  errorMessage,
  onRemove,
}: AttachmentChipProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const isImage = IMAGE_MIMES.has(file.type);

  useEffect(() => {
    if (!isImage) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file, isImage]);

  const sizeKb = (file.size / 1024).toFixed(1);
  const errorLabel = errorCodeToLabel(errorCode);
  const containerClass = errorCode
    ? 'inline-flex items-center gap-2 px-2 py-1 rounded-lg border text-xs border-red-300 bg-red-50'
    : 'inline-flex items-center gap-2 px-2 py-1 rounded-lg border text-xs border-gray-200 bg-gray-50';

  return (
    <div className={containerClass} data-testid="attachment-chip">
      {isImage && blobUrl ? (
        <img
          src={blobUrl}
          alt={file.name}
          className="w-8 h-8 object-cover rounded"
        />
      ) : (
        <PaperClipIcon className="w-4 h-4 text-gray-500" aria-hidden />
      )}
      <span
        className="font-medium text-gray-800 truncate max-w-[200px]"
        title={file.name}
      >
        {file.name}
      </span>
      <span className="text-gray-500">{sizeKb} KB</span>
      {errorCode && (
        <span className="text-red-700" title={errorMessage ?? undefined}>
          · {errorLabel}
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${file.name}`}
          className="ml-1 text-gray-400 hover:text-gray-700"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function errorCodeToLabel(code?: string | null): string {
  switch (code) {
    case 'ATTACHMENT_TOO_LARGE':
      return 'Too large (>25 MB)';
    case 'ATTACHMENT_DISALLOWED_TYPE':
      return 'Type not allowed';
    case 'ATTACHMENT_TOO_MANY':
      return 'Too many attachments';
    default:
      return code ? 'Upload failed' : '';
  }
}
